import { PunchStatus, PunchType } from './enums';
const byTime = (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime();
// ---------------------------------------------------------------- 配对
const DEFAULT_MAX_SESSION_MINUTES = 16 * 60;
/**
 * 把打卡序列配成一段段工时。
 *
 * 规则：
 * - 连续两次 IN：前一次 IN 记为「有上班没下班」，后一次接着等 OUT。
 * - OUT 前面没有 IN：记为「有下班没上班」。
 * - 配上但超过单段上限：整段作废，两条打卡都标记异常。
 */
export function pairPunches(punches, options = {}) {
    const allowed = options.includeStatuses ?? [PunchStatus.APPROVED];
    const maxSession = options.maxSessionMinutes ?? DEFAULT_MAX_SESSION_MINUTES;
    const sorted = punches
        .filter((p) => allowed.includes(p.status))
        .slice()
        .sort(byTime);
    const sessions = [];
    const anomalies = [];
    let pendingIn = null;
    for (const punch of sorted) {
        if (punch.type === PunchType.IN) {
            if (pendingIn) {
                anomalies.push({
                    punchId: pendingIn.id,
                    type: pendingIn.type,
                    occurredAt: pendingIn.occurredAt,
                    reason: 'MISSING_CLOCK_OUT',
                });
            }
            pendingIn = punch;
            continue;
        }
        // OUT
        if (!pendingIn) {
            anomalies.push({
                punchId: punch.id,
                type: punch.type,
                occurredAt: punch.occurredAt,
                reason: 'MISSING_CLOCK_IN',
            });
            continue;
        }
        const minutes = (punch.occurredAt.getTime() - pendingIn.occurredAt.getTime()) / 60_000;
        if (minutes > maxSession) {
            anomalies.push({
                punchId: pendingIn.id,
                type: pendingIn.type,
                occurredAt: pendingIn.occurredAt,
                reason: 'SESSION_TOO_LONG',
                minutes,
            }, {
                punchId: punch.id,
                type: punch.type,
                occurredAt: punch.occurredAt,
                reason: 'SESSION_TOO_LONG',
                minutes,
            });
        }
        else {
            sessions.push({
                startPunchId: pendingIn.id,
                endPunchId: punch.id,
                start: pendingIn.occurredAt,
                end: punch.occurredAt,
                minutes,
            });
        }
        pendingIn = null;
    }
    let openShift = null;
    if (pendingIn) {
        const elapsed = options.now ? (options.now.getTime() - pendingIn.occurredAt.getTime()) / 60_000 : null;
        if (elapsed !== null && elapsed <= maxSession) {
            openShift = pendingIn;
        }
        else {
            anomalies.push({
                punchId: pendingIn.id,
                type: pendingIn.type,
                occurredAt: pendingIn.occurredAt,
                reason: 'MISSING_CLOCK_OUT',
            });
        }
    }
    return { sessions, anomalies, openShift };
}
/**
 * 一个人在一段时间里的考勤汇总。
 *
 * **配对要拿整段区间的打卡一起做，不能按天切开再各自配对** ——
 * 按天切的话，跨零点的夜班在两天里各剩一条孤零零的打卡，
 * 两天都会报「忘打卡」，而实际上它们是完整的一对。
 * 区间的两头也一样，所以按区间统计时要传 `range`、打卡多取（CR-066）。
 *
 * @param dayKey 把时刻换成工地时区自然日的函数（传 `dayKeyInTimeZone` 的偏应用）
 */
export function summarizeAttendance(punches, dayKey, options = {}) {
    const { sessions, anomalies, openShift } = pairPunches(punches, options);
    const punchDays = attributePunchDays(punches, dayKey);
    const range = options.range;
    const inRange = (date) => !range || (date >= range.from && date <= range.to);
    const byDate = new Map();
    let workedMinutes = 0;
    for (const session of sessions) {
        const date = dayKey(session.start);
        if (!inRange(date))
            continue;
        const day = byDate.get(date) ?? { date, minutes: 0, sessions: [] };
        day.minutes += session.minutes;
        day.sessions.push(session);
        byDate.set(date, day);
        workedMinutes += session.minutes;
    }
    const dayStatuses = options.dayStatuses ?? options.includeStatuses ?? [PunchStatus.APPROVED];
    const workedDates = new Set();
    for (const punch of punches) {
        if (punch.type !== PunchType.IN)
            continue;
        if (!dayStatuses.includes(punch.status))
            continue;
        const date = dayKey(punch.occurredAt);
        if (inRange(date))
            workedDates.add(date);
    }
    return {
        workedDays: workedDates.size,
        workedMinutes,
        days: [...byDate.values()].sort((a, b) => (a.date < b.date ? 1 : -1)),
        anomalies: anomalies.filter((a) => inRange(punchDays.get(a.punchId) ?? dayKey(a.occurredAt))),
        openShift: openShift && inRange(dayKey(openShift.occurredAt)) ? openShift : null,
        punchDays,
    };
}
// ---------------------------------------------------------------- 跨零点（CR-066）
/**
 * 按区间查打卡时，前后各多取一个最长班次。
 *
 * 跨过区间边界的合法班次不会超过最长班次，所以另一张卡一定落在这个窗口里。
 * 多取进来的卡只用来配对，统计时按 `summarizeAttendance` 的 `range` / `attributePunchDays` 过滤掉。
 */
export function shiftWindow(start, end, maxShiftMinutes) {
    const pad = maxShiftMinutes * 60_000;
    return { start: new Date(start.getTime() - pad), end: new Date(end.getTime() + pad) };
}
/**
 * 每张卡归属哪一天（工地时区的 `YYYY-MM-DD`）。
 *
 * 和工时表画线同一个口径：不含已拒绝的卡，按时间顺序，**上班后面紧跟着下班 → 两张都归上班那天**
 * （不管隔多久，超长的那段也画在上班那天）；其余的卡（已拒绝的、没配上的）归自己那天。
 * 夜班 9/13 23:00 → 9/14 07:00 的下班卡因此列在 9/13 那天，而不是 9/14 多出一行。
 */
export function attributePunchDays(punches, dayKey) {
    const days = new Map();
    let pendingIn = null;
    for (const punch of punches.slice().sort(byTime)) {
        days.set(punch.id, dayKey(punch.occurredAt));
        if (punch.status === PunchStatus.REJECTED)
            continue;
        if (punch.type === PunchType.IN) {
            pendingIn = punch;
        }
        else if (pendingIn) {
            days.set(punch.id, dayKey(pendingIn.occurredAt));
            pendingIn = null;
        }
    }
    return days;
}
/** 补录的时刻最多比「现在」晚这么多：管理员电脑和服务器的时钟差一点，不算补了未来 */
export const MANUAL_PUNCH_FUTURE_TOLERANCE_MS = 5 * 60_000;
/**
 * 补录的卡和这个人已有的卡合不合得上（CR-066）。
 *
 * 补录表单当场检查、服务端建卡之前再查一次，**用的是同一份**。
 * 改之前表单按「当天第几分钟」推 ±1 天、服务端什么都不查：
 * 夜班选错一天会补进后一晚那班里，还能补出未来的卡，把正在上班的人的班次闭掉。
 *
 * @param existing 这个人在这个工地的卡（已拒绝的会被忽略；待复核的算 —— 工时表照样画它）
 * @param additions 一张（补上班 / 补下班），或两张（补整段：上班在前、下班在后）
 */
export function checkManualPunches(existing, additions, options) {
    const first = additions[0];
    const last = additions[additions.length - 1];
    if (!first || !last)
        return null;
    const max = options.maxShiftMinutes * 60_000;
    const now = options.now.getTime();
    const time = (p) => p.occurredAt.getTime();
    const start = first.at.getTime();
    const end = last.at.getTime();
    if (additions.some((a) => a.at.getTime() > now + MANUAL_PUNCH_FUTURE_TOLERANCE_MS))
        return 'future';
    const pair = additions.length > 1;
    if (pair && (first.type !== PunchType.IN || last.type !== PunchType.OUT || end <= start))
        return 'order';
    if (pair && end - start > max)
        return 'tooLong';
    const rows = existing.filter((p) => p.status !== PunchStatus.REJECTED).slice().sort(byTime);
    if (rows.some((p) => time(p) >= start && time(p) <= end))
        return 'overlap';
    const before = rows.filter((p) => time(p) < start).pop() ?? null;
    const after = rows.find((p) => time(p) > end) ?? null;
    if (before?.type === PunchType.IN) {
        // 前后两张本来就是一段合法的班次：往中间插什么都会把它拆坏
        const insideValid = after?.type === PunchType.OUT && time(after) - time(before) <= max;
        // 他正在上班：往后补上班卡或整段，会把这一班闭掉（手机上显示已下班、工地解锁）
        const stillOpen = after === null && now - time(before) <= max;
        if (insideValid || (first.type === PunchType.IN && stillOpen))
            return 'overlap';
    }
    if (pair)
        return null;
    if (first.type === PunchType.OUT) {
        if (before?.type !== PunchType.IN)
            return 'noOpenIn';
        return start - time(before) > max ? 'tooLong' : null;
    }
    if (after?.type === PunchType.OUT)
        return time(after) - start > max ? 'tooLong' : null;
    if (after?.type === PunchType.IN)
        return 'noOpenOut';
    // 后面一张卡都没有：只有「忘了打上班卡、人还在干活」说得通
    return now - start > max ? 'noOpenOut' : null;
}
// ---------------------------------------------------------------- 工地概览（CR-066）
/**
 * 工地概览的「今日在岗 / 已打卡」。
 *
 * - **在岗**：这个人在本工地最后一张没被拒的卡是上班卡，而且离现在不超过最长班次 ——
 *   改之前按「今天最后一张是上班卡」算，夜班过了零点就不算在岗了；
 * - **已打卡**：今天（工地时区）打过上班卡的人，加上在岗的人。早上刚下夜班、今天没再上班的不算。
 *
 * @param punches 本工地的卡，至少覆盖「今天零点」和「现在减一个最长班次」里更早的那一刻起
 */
export function sitePresence(punches, options) {
    const max = options.maxShiftMinutes * 60_000;
    const lastByUser = new Map();
    const checkedIn = new Set();
    for (const punch of punches.filter((p) => p.status !== PunchStatus.REJECTED).sort(byTime)) {
        lastByUser.set(punch.userId, punch);
        if (punch.type === PunchType.IN && punch.occurredAt.getTime() >= options.dayStart.getTime()) {
            checkedIn.add(punch.userId);
        }
    }
    let onSite = 0;
    for (const [userId, punch] of lastByUser) {
        if (punch.type === PunchType.IN && options.now.getTime() - punch.occurredAt.getTime() <= max) {
            onSite += 1;
            checkedIn.add(userId);
        }
    }
    return { onSite, checkedIn: checkedIn.size };
}
//# sourceMappingURL=attendance.js.map